const { useEffect, useState, useMemo } = require("react")
const { useSelector } = require("react-redux")

const mime = require("mime-types");
const Path = require("path")

const projectApi = require("../../../../api").project
const fileApi = require("../../../../api").file

function fileToBuffer(file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

function blobStringify(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
}

function b64ToBlob(file, opts) {
  const byteChars = atob(file)
  const byteArrays = []
  for (let offset = 0; offset < byteChars.length; offset += 512) {
    const slice = byteChars.slice(offset, offset + 512)
    const byteNums = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNums[i] = slice.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNums)
    byteArrays.push(byteArray)
  }
  return new Blob(byteArrays, opts)
}

function sizeMagn(size) {
  size = Number(size);
  let magn = Math.log10(size);
  if (!Number.isNaN(magn)) {
    if (magn < 3) return 0;
    if (magn >= 3 && magn < 6) return 3;
    if (magn >= 6 && magn < 9) return 6;
    if (magn >= 9 && magn < 12) return 9;
    if (magn > 12) return 12;
  } else return "NaN";
}

function sizeUnit(magn) {
  switch (magn) {
    case 0:
      return "B";
    case 3:
      return "Kb";
    case 6:
      return "Mb";
    case 9:
      return "Gb";
    case 12:
      return "Tb";
    default:
      return "";
  }
}


function closeAlert(e) {
  e = e || window.event;
  // IE and Firefox
  if (e) e.returnValue = "A file is currently uploading";
  // Safari
  return "A file is currently uploading";
}

function* chunkSlicer(file, chunksize = 8192, fileReader = new FileReader()) {
  for (let start = 0; start < file.byteLength; start += chunksize) {
    const end = Math.min(file.byteLength, start + chunksize)
    yield new Promise((resolve, reject) => {
      fileReader.readAsArrayBuffer(file.slice(start, end))
      fileReader.onload = () => resolve(fileReader.result)
      fileReader.onerror = reject
    })
  }
}

function useUploadStream(file, projectUid) {

  const [progress, setProgress] = useState({
    status: "init",
    uploaded: 0,
    total: 0,
    percent: 0,
    fileUid: null
  })

  // file meta data
  const meta = useMemo(() => {
    const ext = mime.extension(file.type)
    // compatibility property byteLength between File and ArrayBuffer
    file.byteLength = file.size
    return {
      // file extension
      ext,
      // filename without extension
      name: ext ? Path.basename(file.name, `.${ext}`) : file.name,
      data: file
    }
  }, [file])

  // launch stream upload
  useEffect(() => {
    if (meta.data && progress.status === "init") {
      setProgress(p => ({ ...p, status: "started" }))
      // set stream upload params
      const params = {
        slicer: chunkSlicer,
        chunksize: 100000, // 100 kB
        onProgress: (uploaded, total) => setProgress(p => ({
          ...p, uploaded, total,
          percent: uploaded && total ? Math.round(uploaded / total * 100) : 0
        }))
      }
      // window alert before tab is closing
      window.onbeforeunload = closeAlert
      // project upload file api call
      projectApi.uploadFile(projectUid, meta, params)
        .then(fileUid => {
          meta.uid = fileUid
          setProgress(p => ({ ...p, status: "finished" }))
        })
        .catch(err => {
          setProgress(p => ({ ...p, status: "error" }))
          console.error(err)
        })
        .finally(() => window.onbeforeunload = null)
    }
  }, [projectUid, meta, progress.status])

  return [meta, progress]
}

function useDownloadStream(fileUid) {

  const [progress, setProgress] = useState({
    status: "init",
    downloaded: 0,
    total: 0,
    percent: 0,
    blob: null
  })
  // get file meta data
  const file = useSelector(state => state.list.file[fileUid])

  // launch file download stream
  useEffect(() => {
    if (file && progress.status === "init") {
      // progress start
      setProgress(prev => ({
        ...prev,
        status: "started",
        total: file.size
      }))
      // window alert before tab is closing
      window.onbeforeunload = closeAlert
      // file download api call
      fileApi.streamRead(
        file.uid,
        128 * 1024,
        chunksArray => new Blob(chunksArray, { type: mime.lookup(file.ext) }),
        downloaded => setProgress(prev => ({
          ...prev, downloaded,
          percent: downloaded && prev.total ? Math.round(downloaded / prev.total * 100) : 0
        }))
      )
        .then(blob => setProgress(prev => ({ ...prev, status: "finished", blob })))
        .catch(err => {
          setProgress(prev => ({ ...prev, status: "error" }))
          console.error(err)
        })
        .finally(() => window.onbeforeunload = null)
    }
  }, [file, progress.status])

  return progress
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const dl = document.createElement('a');
  dl.setAttribute('href', url);
  dl.setAttribute('download', fileName);
  dl.style.display = 'none';
  document.body.appendChild(dl);
  dl.click();
  document.body.removeChild(dl);
  window.URL.revokeObjectURL(url);
}

module.exports = {
  ...require("./script"),
  useIcon: require("./useIcon"),
  fileToBuffer,
  blobStringify,
  b64ToBlob,
  saveBlob,
  sizeMagn,
  sizeUnit,
  chunkSlicer,
  useUploadStream,
  useDownloadStream,
  fileToSend(file) {
    const ext = mime.extension(file.type)
    const name = ext ? Path.basename(file.name, `.${ext}`) : file.name
    return new Promise((resolve, reject) => {
      fileToBuffer(file).then(buffer => {
        resolve({
          name,
          ext,
          size: file.size,
          data: buffer
        })
      }).catch(reject);
    });
  },
  sizeConvert(size) {
    let magn = sizeMagn(size);
    let unit = sizeUnit(magn);
    let converter = Math.pow(10, magn);
    size = (Math.round(size) / converter).toPrecision(5);
    return `${size} ${unit}`;
  }
}
